import { Translator } from 'Translator';
import { closestCity$ } from 'modules/homescreen/hoc/WithSearchNearby';
import PermissionsProvider from 'providers/PermissionsProvider';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import TimeGuard from 'utils/TimeGuard';
import slugify from 'utils/slugify';
import { useAccount, useUser } from '~/authentication/store';

import { LocationPermissions } from '../../cordovaPlugins/Permissions';
import IncludeOnPlatforms from '../../hoc/IncludeOnPlatforms';
import { appServices } from '../../singletons';
import Banner from '../Banner';

const CSS = require('./LocationBanner.less');
const timeGuard = TimeGuard.create('homescreen-permissions-banner-request');

interface LocationBannerProps {
  redirectToSearch?: boolean;
}

const TIMEOUT_IN_DAYS = 60;

const LocationBanner = ({ redirectToSearch }: LocationBannerProps) => {
  const history = useHistory();
  const [visible, setVisible] = useState(true);
  const { isHost } = useUser();
  const { account } = useAccount();

  const disallow = () => {
    timeGuard.setTimeoutForDays(TIMEOUT_IN_DAYS);
    setVisible(false);
  };

  const onPermissions = (given) => {
    if (given) {
      if (redirectToSearch) {
        closestCity$
          .take(1)
          .toPromise()
          .then(({ city }) => {
            if (city) {
              history.push(
                `/s/${slugify(city.location.country, false)}/${slugify(
                  city.location.city,
                  false,
                )}/`,
              );
            }
          });
      }
    } else {
      timeGuard.setTimeoutForDays(TIMEOUT_IN_DAYS);
    }
  };

  const createAllowHandler = (location: LocationPermissions, push: boolean) => {
    const { permissionFunctions } = appServices;
    return () => {
      const promise = location.canRequestBackground
        ? permissionFunctions.requestLocationBackgroundPermission()
        : permissionFunctions.askForLocationPermissionsAfterDenial();

      promise
        .then((success) => {
          if (success && !push) {
            return permissionFunctions.startPush();
          }
          return success;
        })
        .then(onPermissions);

      setVisible(false);
    };
  };
  const renderBanner = ({ location, push }) => {
    if (
      account &&
      !isHost &&
      !(location.inUse && push) &&
      timeGuard.check(false)
    ) {
      return (
        <Banner
          className={CSS.ShowNearbyBanner}
          buttons={[
            {
              label: Translator.translate(
                'UserEnvironment.LocationBanner.no',
                {},
                false,
                'No, thanks',
              ),
              onClick: disallow,
            },
            {
              label: Translator.translate(
                'UserEnvironment.LocationBanner.yes',
                {},
                false,
                'Ok, sure',
              ),
              onClick: createAllowHandler(location, push),
              isDefault: true,
            },
          ]}
        >
          <div>
            <p className={CSS.Title}>
              {Translator.translate(
                'UserEnvironment.LocationBanner.title',
                {},
                false,
                'Get notified when there are cool things to do nearby',
              )}
            </p>
            <p className={CSS.Explanation}>
              {Translator.translate(
                'UserEnvironment.LocationBanner.explanation',
                {},
                false,
                "We'll need your location for this. Don't worry we will not store it.",
              )}
            </p>
          </div>
        </Banner>
      );
    }
    return null;
  };

  if (visible) {
    return <PermissionsProvider>{renderBanner}</PermissionsProvider>;
  }
  return null;
};

export default IncludeOnPlatforms(['cordova'])(LocationBanner);
